/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { useForm } from '../components/useForm';
import Modal from '../components/Modal';
import EditSettingsForm from '../content/EditSettingsForm';
import EditSettingsOptions from '../content/EditSettingsOptions';
import { Controls } from '../components/controls/Controls';
import { getCategorySettingOrder, getSettingsForIntakeSubcategory } from '../utils/categorySettingOrder';

const initialFieldValues = {
  name: '',
  category: '',
  value: '',
};

export default function CustomerSettingsPane(props) {
  const {
    onSettingSave, category, subCategory,
    customer, refreshParent, setErrorMessage, setSuccessMessage,
  } = props;
  const [configuredSettings, setConfiguredSettings] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { values, setValues, handleInputChange } = useForm(initialFieldValues);

  const handleModalClose = () => {
    setIsEditModalOpen(false);
    setValues(initialFieldValues);
  };

  const handleSettingSave = () => {
    setIsEditModalOpen(false);
    onSettingSave(values);
    setValues(initialFieldValues);
  };

  /**
 * Fetches settings for the selected category with fixed ordering.
 *
 * If there are no customer settings or the necessary subcategory selection is missing,
 * it returns an empty array. Otherwise, it filters the customer settings based on
 * the category and optionally the subcategory, then sorts them according to the
 * predefined order.
 */
  const fetchSettingsForSelectedCategory = () => {
    // If customer settings are not present, return an empty array.
    if (!customer.settings || !category) {
      return [];
    }

    // Filter settings based on category and optionally subcategory.
    const filterByCategory = (setting) => {
      if (setting.category !== category.id) return false;

      if (!subCategory) return true;

      // Special handling for intake subcategories
      if (category.id === 'intake') {
        const allowedSettings = getSettingsForIntakeSubcategory(subCategory.id);
        return allowedSettings.includes(setting.name);
      }

      // Default behavior for other categories (like API)
      return setting.name.includes(subCategory.id);
    };

    const filteredSettings = customer.settings.filter(filterByCategory);

    // Get the fixed order for the current category
    const categorySettingOrder = getCategorySettingOrder(category.id);
    const positionMap = {};
    categorySettingOrder.forEach((name, index) => {
      positionMap[name] = index;
    });

    // Sort settings by their predefined order
    return filteredSettings.sort((a, b) => {
      const posA = positionMap[a.name] !== undefined ? positionMap[a.name] : 99999;
      const posB = positionMap[b.name] !== undefined ? positionMap[b.name] : 99999;

      if (posA !== posB) {
        return posA - posB;
      }
      // If both are not in the predefined order, sort alphabetically
      return a.name.localeCompare(b.name);
    });
  };

  useEffect(() => {
    const settings = fetchSettingsForSelectedCategory();
    setConfiguredSettings(settings);
  }, [customer, category, subCategory]);

  if (!customer) {
    return null;
  }

  return (
    <div style={{ overflow: 'auto' }}>
      <div style={{ width: '100%' }}>
        {configuredSettings.map((setting, index) => (
          <Grid container spacing={2} key={`${setting.name}-${index}`} style={{ marginBottom: '30px', marginTop: '10px' }}>
            <Grid item>
              <Controls.Input
                value={setting.name}
                name="name"
                label="Name"
                variant="outlined"
                disabled
                width="370px"
              />
            </Grid>
            <Grid item>
              <EditSettingsOptions
                setValues={setValues}
                handleSaveClick={handleSettingSave}
                handleEditClick={() => {
                  setIsEditModalOpen(true);
                  setValues(setting);
                }}
                values={setting}
                refreshParent={refreshParent}
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
                handleInputChange={handleInputChange}
                contextType="customer"
              />
            </Grid>
          </Grid>
        ))}
      </div>
      <Modal isOpen={isEditModalOpen} handleClose={handleModalClose}>
        <Grid container>
          <Grid item xs={6}>
            <EditSettingsForm
              handleInputChange={handleInputChange}
              values={values}
              handleSaveClick={handleSettingSave}
            />
          </Grid>
        </Grid>
      </Modal>
    </div>
  );
}

CustomerSettingsPane.propTypes = {
  onSettingSave: PropTypes.func.isRequired,
  category: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    subCategories: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
    })),
  }).isRequired,
  customer: PropTypes.shape({
    settings: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      category: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })),
  }),
  refreshParent: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
  subCategory: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  }),
};

CustomerSettingsPane.defaultProps = {
  customer: null,
  subCategory: null,
};
